/**
 * Global Reactive Cart Store for frontend-v2
 * Implements Milestone M4:
 * - Reactive state: items, appliedDiscount, customerUsername, friendshipStatus
 * - Full Actions: addItem, removeItem, updateQuantity, clearCart, applyCoupon, removeCoupon, setUsername
 * - Computeds: totalVbucks, basePrice, discountAmount, finalPrice reactive to useCurrencyStore
 * - Fixed Recharge Overrides: recharge_vbucks_12500 and recharge_crew
 * - LocalStorage persistence: fn_cart, fn_epic_username, fn_applied_discount with cross-tab sync
 */

import { create } from 'zustand';
import { useMemo } from 'react';
import { CartItem, ShopItem, SupportedCurrency, FriendshipStatus } from '../types';
import { useCurrencyStore } from './useCurrencyStore';
import { calculateFiatPrice, sanitizeCurrency } from '../utils/currency';
import { validateCoupon, DiscountApiError } from '../services/discountApi';

export const CART_STORAGE_KEY = 'fn_cart';
export const USERNAME_STORAGE_KEY = 'fn_epic_username';
export const DISCOUNT_STORAGE_KEY = 'fn_applied_discount';

export const RECHARGE_OFFER_IDS = new Set(['recharge_vbucks_12500', 'recharge_crew']);

export interface DiscountInfo {
  code: string;
  discount_pct: number;
  discount_amount?: number;
  base_price?: number;
  final_price?: number;
  currency?: SupportedCurrency;
  message?: string;
}

export interface CartTotals {
  totalVbucks: number;
  basePrice: number;
  discountAmount: number;
  finalPrice: number;
  currency: SupportedCurrency;
  itemCount: number;
}

export interface CartState {
  // State
  items: CartItem[];
  appliedDiscount: DiscountInfo | null;
  customerUsername: string;
  friendshipStatus: FriendshipStatus | null;
  isCheckingCoupon: boolean;
  couponError: string | null;

  // Actions
  addItem: (item: ShopItem | CartItem, quantity?: number) => void;
  removeItem: (offerId: string) => void;
  updateQuantity: (offerId: string, quantity: number) => void;
  clearCart: () => void;
  setUsername: (username: string) => void;
  setFriendshipStatus: (status: FriendshipStatus | null) => void;
  applyCoupon: (code: string) => Promise<{ success: boolean; message: string; discount?: DiscountInfo }>;
  removeCoupon: () => void;
  clearCouponError: () => void;

  // Imperative Getters
  isInCart: (offerId: string) => boolean;
  getItemCount: () => number;
  getTotalVbucks: () => number;
  getBasePrice: (currency?: SupportedCurrency) => number;
  getDiscountAmount: (currency?: SupportedCurrency) => number;
  getFinalPrice: (currency?: SupportedCurrency) => number;
  getTotals: (currency?: SupportedCurrency) => CartTotals;
  isOnlyRecharges: () => boolean;
}

/**
 * Checks if a given offerId is a direct recharge item.
 */
export function isRechargeOffer(offerId: string): boolean {
  return RECHARGE_OFFER_IDS.has(offerId) || offerId.startsWith('recharge_');
}

/**
 * Returns true if the cart is not empty and consists solely of direct recharge items.
 * (Direct recharges bypass the 48-hour bot friendship verification requirement).
 */
export function isCartOnlyRecharges(items: CartItem[]): boolean {
  return items.length > 0 && items.every((item) => isRechargeOffer(item.offerId));
}

/**
 * Resolves cosmetic display image defensively.
 */
export function resolveItemImage(item: ShopItem | CartItem): string {
  const offerId = 'offerId' in item ? item.offerId : (item as any).id;
  if (offerId === 'recharge_vbucks_12500') {
    return 'https://fortnite-api.com/images/vbuck.png';
  }
  if ('image' in item && typeof item.image === 'string' && item.image.length > 0) {
    if (offerId?.startsWith('recharge_') && item.image.includes('cid_016_athena_commando_f')) {
      return 'https://fortnite-api.com/images/vbuck.png';
    }
    return item.image;
  }
  if ('images' in item && item.images) {
    if (item.images.icon) return item.images.icon;
    if (item.images.featured) return item.images.featured;
    if (item.images.background) return item.images.background;
  }
  if (offerId?.startsWith('recharge_')) {
    return 'https://fortnite-api.com/images/vbuck.png';
  }
  return 'https://fortnite-api.com/images/cosmetics/br/cid_a_457_athena_commando_m_cirrusvine/icon.png';
}

/**
 * Pure calculation engine for cart totals in a specific currency.
 */
export function calculateCartTotals(
  items: CartItem[],
  appliedDiscount: DiscountInfo | null,
  currency: SupportedCurrency
): CartTotals {
  const safeCurrency = sanitizeCurrency(currency);

  let totalVbucks = 0;
  let basePrice = 0;
  let itemCount = 0;

  for (const item of items) {
    const qty = typeof item.quantity === 'number' && item.quantity > 0 ? item.quantity : 1;
    const vbucks = typeof item.vbucks === 'number' && Number.isFinite(item.vbucks) ? item.vbucks : 0;
    const unitPrice = calculateFiatPrice(vbucks, safeCurrency, item.offerId);

    totalVbucks += vbucks * qty;
    basePrice += unitPrice * qty;
    itemCount += qty;
  }

  let discountAmount = 0;
  if (appliedDiscount && appliedDiscount.discount_pct > 0 && totalVbucks > 0) {
    discountAmount = Math.round(basePrice * (appliedDiscount.discount_pct / 100.0));
  }

  const finalPrice = Math.max(0, basePrice - discountAmount);

  return {
    totalVbucks,
    basePrice,
    discountAmount,
    finalPrice,
    currency: safeCurrency,
    itemCount,
  };
}

/**
 * Defensively reads and normalizes cart items from localStorage.
 * Handles legacy shapes (item.id, item.finalPrice) and corrupted values.
 */
export function readAndSanitizeStoredCart(): CartItem[] {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = window.localStorage.getItem(CART_STORAGE_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return sanitizeCartArray(parsed);
      }
    }
  } catch {
    // Storage access denied or corrupted JSON
  }
  return [];
}

/**
 * Normalizes an array of raw items into clean CartItem structures.
 */
export function sanitizeCartArray(rawArray: any[]): CartItem[] {
  if (!Array.isArray(rawArray)) return [];

  const items: CartItem[] = [];
  for (const raw of rawArray) {
    if (!raw || typeof raw !== 'object') continue;
    const offerId = String(raw.offerId || raw.offer_id || raw.id || '').trim();
    if (!offerId) continue;

    const rawVbucks = raw.vbucks ?? raw.finalPrice ?? 0;
    const vbucks = typeof rawVbucks === 'number' && Number.isFinite(rawVbucks) ? Math.max(0, rawVbucks) : 0;
    const rawQty = raw.quantity;
    const quantity = typeof rawQty === 'number' && Number.isFinite(rawQty) && rawQty > 0 ? Math.floor(rawQty) : 1;

    let image = raw.image || raw.images?.icon || raw.images?.featured;
    if (
      offerId === 'recharge_vbucks_12500' ||
      offerId === 'recharge_crew' ||
      offerId.startsWith('recharge_') ||
      image?.includes('cid_016_athena_commando_f') ||
      image?.includes('character_faintdoor')
    ) {
      image = 'https://fortnite-api.com/images/vbuck.png';
    }

    let finalVbucks = vbucks;
    if (offerId === 'recharge_crew') {
      finalVbucks = 800;
    }

    items.push({
      offerId,
      name: String(raw.name || 'Cosmético de Fortnite'),
      vbucks: finalVbucks,
      image,
      rarity: raw.rarity || 'Común',
      quantity,
    });
  }
  return items;
}

/**
 * Reads stored customer username from localStorage.
 */
export function readStoredUsername(): string {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return (window.localStorage.getItem(USERNAME_STORAGE_KEY) || '').trim();
    }
  } catch {}
  return '';
}

/**
 * Reads stored applied discount from sessionStorage (or localStorage fallback).
 */
export function readStoredDiscount(): DiscountInfo | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const stored = window.sessionStorage.getItem(DISCOUNT_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && typeof parsed.code === 'string' && typeof parsed.discount_pct === 'number') {
          return {
            code: parsed.code,
            discount_pct: parsed.discount_pct,
            discount_amount: parsed.discount_amount,
            base_price: parsed.base_price,
            final_price: parsed.final_price,
            currency: sanitizeCurrency(parsed.currency),
            message: parsed.message,
          };
        }
      }
    }
  } catch {}
  return null;
}

/**
 * Persists cart items array to localStorage.
 */
export function persistCart(items: CartItem[]): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      window.dispatchEvent(new CustomEvent('cartUpdated', { detail: items }));
    }
  } catch {}
}

/**
 * Persists Epic Games customer username to localStorage.
 */
export function persistUsername(username: string): void {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(USERNAME_STORAGE_KEY, username.trim());
      window.dispatchEvent(new CustomEvent('epicUsernameChanged', { detail: username.trim() }));
    }
  } catch {}
}

/**
 * Persists or clears applied discount in sessionStorage.
 */
export function persistDiscount(discount: DiscountInfo | null): void {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      if (discount) {
        window.sessionStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(discount));
      } else {
        window.sessionStorage.removeItem(DISCOUNT_STORAGE_KEY);
      }
    }
  } catch {}
}

export const useCartStore = create<CartState>()((set, get) => {
  // Cross-tab and window event listener setup (only in browser environment)
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
      if (event.key === CART_STORAGE_KEY) {
        if (!event.newValue) {
          set({ items: [], appliedDiscount: null });
        } else {
          try {
            const parsed = JSON.parse(event.newValue);
            const sanitized = sanitizeCartArray(parsed);
            set({ items: sanitized });
          } catch {}
        }
      } else if (event.key === USERNAME_STORAGE_KEY) {
        const newUsername = (event.newValue || '').trim();
        if (get().customerUsername !== newUsername) {
          set({ customerUsername: newUsername, friendshipStatus: null });
        }
      }
    });
  }

  return {
    items: readAndSanitizeStoredCart(),
    appliedDiscount: readStoredDiscount(),
    customerUsername: readStoredUsername(),
    friendshipStatus: null,
    isCheckingCoupon: false,
    couponError: null,

    addItem: (item: ShopItem | CartItem, quantity = 1) => {
      const offerId = 'offerId' in item ? item.offerId : item.id;
      if (!offerId) return;

      const safeQty = Math.max(1, Math.floor(quantity));
      const vbucks = 'vbucks' in item && typeof item.vbucks === 'number' ? item.vbucks : (item as any).finalPrice || 0;
      const image = resolveItemImage(item);
      const name = item.name || 'Cosmético de Fortnite';
      const rarity = item.rarity || 'Común';

      const currentItems = get().items;
      const existingIdx = currentItems.findIndex((it) => it.offerId === offerId);

      let nextItems: CartItem[];
      if (existingIdx >= 0) {
        nextItems = currentItems.map((it, idx) =>
          idx === existingIdx ? { ...it, quantity: it.quantity + safeQty } : it
        );
      } else {
        nextItems = [
          ...currentItems,
          {
            offerId,
            name,
            vbucks,
            image,
            rarity,
            quantity: safeQty,
          },
        ];
      }

      persistCart(nextItems);
      set({ items: nextItems });
    },

    removeItem: (offerId: string) => {
      const nextItems = get().items.filter((it) => it.offerId !== offerId);
      persistCart(nextItems);

      // If cart becomes empty, automatically remove discount and friendship status
      if (nextItems.length === 0) {
        persistDiscount(null);
        set({ items: nextItems, appliedDiscount: null, friendshipStatus: null, couponError: null });
      } else {
        set({ items: nextItems });
      }
    },

    updateQuantity: (offerId: string, quantity: number) => {
      const safeQty = Math.floor(quantity);
      if (safeQty <= 0) {
        get().removeItem(offerId);
        return;
      }

      const nextItems = get().items.map((it) =>
        it.offerId === offerId ? { ...it, quantity: safeQty } : it
      );
      persistCart(nextItems);
      set({ items: nextItems });
    },

    clearCart: () => {
      persistCart([]);
      persistDiscount(null);
      set({
        items: [],
        appliedDiscount: null,
        friendshipStatus: null,
        couponError: null,
      });
    },

    setUsername: (username: string) => {
      const clean = (username || '').trim();
      persistUsername(clean);
      set({
        customerUsername: clean,
        // Invalidate previous friendship verification whenever username changes
        friendshipStatus: null,
      });
    },

    setFriendshipStatus: (status: FriendshipStatus | null) => {
      set({ friendshipStatus: status });
    },

    applyCoupon: async (codeStr: string) => {
      const code = (codeStr || '').trim().toUpperCase();
      if (!code) {
        const errorMsg = 'Ingresa un código de descuento.';
        set({ couponError: errorMsg });
        return { success: false, message: errorMsg };
      }

      const currentItems = get().items;
      const totalVbucks = currentItems.reduce(
        (sum, it) => sum + (it.vbucks || 0) * (it.quantity || 1),
        0
      );

      if (totalVbucks <= 0 || currentItems.length === 0) {
        const errorMsg = 'Agrega objetos a tu carrito antes de aplicar un código.';
        set({ couponError: errorMsg });
        return { success: false, message: errorMsg };
      }

      const currentCurrency = useCurrencyStore.getState().currency;
      const customerUsername = get().customerUsername;

      set({ isCheckingCoupon: true, couponError: null });

      try {
        const result = await validateCoupon({
          code,
          total_vbucks: totalVbucks,
          currency: currentCurrency,
          customer_username: customerUsername || undefined,
        });

        const discountInfo: DiscountInfo = {
          code: result.code,
          discount_pct: result.discount_pct,
          discount_amount: result.discount_amount,
          base_price: result.base_price,
          final_price: result.final_price,
          currency: result.currency,
          message: result.message,
        };

        persistDiscount(discountInfo);
        set({
          appliedDiscount: discountInfo,
          isCheckingCoupon: false,
          couponError: null,
        });

        return {
          success: true,
          message: result.message || `¡Código ${result.code} aplicado (-${result.discount_pct}%)!`,
          discount: discountInfo,
        };
      } catch (err: any) {
        const message =
          err instanceof DiscountApiError
            ? err.message
            : err?.message || 'Código de descuento inválido o expirado.';

        persistDiscount(null);
        set({
          appliedDiscount: null,
          isCheckingCoupon: false,
          couponError: message,
        });

        return { success: false, message };
      }
    },

    removeCoupon: () => {
      persistDiscount(null);
      set({ appliedDiscount: null, couponError: null });
    },

    clearCouponError: () => {
      set({ couponError: null });
    },

    isInCart: (offerId: string) => {
      return get().items.some((it) => it.offerId === offerId);
    },

    getItemCount: () => {
      return get().items.reduce((acc, it) => acc + (it.quantity || 1), 0);
    },

    getTotalVbucks: () => {
      return calculateCartTotals(
        get().items,
        get().appliedDiscount,
        useCurrencyStore.getState().currency
      ).totalVbucks;
    },

    getBasePrice: (currency?: SupportedCurrency) => {
      const activeCurr = currency || useCurrencyStore.getState().currency;
      return calculateCartTotals(get().items, get().appliedDiscount, activeCurr).basePrice;
    },

    getDiscountAmount: (currency?: SupportedCurrency) => {
      const activeCurr = currency || useCurrencyStore.getState().currency;
      return calculateCartTotals(get().items, get().appliedDiscount, activeCurr).discountAmount;
    },

    getFinalPrice: (currency?: SupportedCurrency) => {
      const activeCurr = currency || useCurrencyStore.getState().currency;
      return calculateCartTotals(get().items, get().appliedDiscount, activeCurr).finalPrice;
    },

    getTotals: (currency?: SupportedCurrency) => {
      const activeCurr = currency || useCurrencyStore.getState().currency;
      return calculateCartTotals(get().items, get().appliedDiscount, activeCurr);
    },

    isOnlyRecharges: () => {
      return isCartOnlyRecharges(get().items);
    },
  };
});

/**
 * Reactive React hook that couples useCartStore with useCurrencyStore.
 * Guarantees instantaneous, reactive recalculation whenever currency toggles (ARS <-> MXN)
 * or cart items/discounts change, with zero manual event listener boilerplate.
 */
export function useCartTotals(): CartTotals {
  const currency = useCurrencyStore((s) => s.currency);
  const items = useCartStore((s) => s.items);
  const appliedDiscount = useCartStore((s) => s.appliedDiscount);

  return useMemo(() => {
    return calculateCartTotals(items, appliedDiscount, currency);
  }, [items, appliedDiscount, currency]);
}

export default useCartStore;
